import React, { useEffect } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { usePrivy } from "@privy-io/react-auth";
import { useQuery, usePaginatedQuery } from "convex/react";
import { api } from "../convex/_generated/api";
import { useAuthData } from "../hooks/useAuthData";
import GooglePlaceDetailsCard from "../components/InternalTool/GooglePlaceDetailsCard";
import { SourceLegend } from "../components/InternalTool/SourceLegend";

const PAGE_SIZE = 10;

export default function InternalToolScreen() {
  const { ready, authenticated, login, user } = usePrivy();
  const { authData } = useAuthData();

  const privyDid = authData?.privyDid || user?.id || null;
  const userDoc = useQuery(
    privyDid ? api.users.getUserFromPrivyDid : "skip",
    privyDid ? { privyDid } : "skip"
  );

  const isDeveloper = !!userDoc?.isDeveloper;
  const placeIdsResult = usePaginatedQuery(
    api.getGooglePlaces.getRestaurantGooglePlaceIdsPaginated,
    isDeveloper ? {} : "skip",
    { initialNumItems: PAGE_SIZE }
  );

  const shouldRedirect =
    ready && authenticated && userDoc !== undefined && !userDoc?.isDeveloper;

  useEffect(() => {
    if (shouldRedirect && typeof window !== "undefined") {
      window.location.replace("/");
    }
  }, [shouldRedirect]);

  if (!ready || (authenticated && userDoc === undefined) || shouldRedirect) {
    return (
      <View style={styles.container}>
        <ActivityIndicator size="large" color="#4e2916" />
        <Text style={styles.loadingText}>Loading...</Text>
      </View>
    );
  }

  if (!authenticated) {
    return (
      <View style={styles.container}>
        <View style={styles.headerCard}>
          <Text style={styles.title}>Internal Tool</Text>
          <Text style={styles.subtitle}>Sign in to continue</Text>
          <TouchableOpacity style={styles.button} onPress={() => login()} activeOpacity={0.8}>
            <Text style={styles.buttonText}>Login / Sign Up</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const email =
    user?.email?.address ||
    user?.linked_accounts?.find((acc) => acc.type === "email")?.address ||
    userDoc?.email ||
    null;

  const placeIds = placeIdsResult?.results || [];
  const canLoadMore = placeIdsResult?.status === "CanLoadMore";
  const isLoadingMore = placeIdsResult?.status === "LoadingMore";

  return (
    <ScrollView contentContainerStyle={styles.scrollContent}>
      <View style={styles.headerCard}>
        <Text style={styles.title}>Internal Tool</Text>
        <Text style={styles.subtitle}>
          Signed in as {email || user?.id || "user"}
        </Text>
        <Text style={styles.awesomeText}>You are awesome!</Text>
        <SourceLegend />
      </View>

      <Text style={styles.sectionTitle}>Restaurants</Text>

      {placeIdsResult?.status === "LoadingFirstPage" ? (
        <ActivityIndicator size="small" color="#4e2916" style={{ marginTop: 12 }} />
      ) : placeIds.length === 0 ? (
        <Text style={styles.emptyText}>No restaurants found</Text>
      ) : (
        <View style={styles.list}>
          {placeIds.map((restaurant) => (
            <GooglePlaceDetailsCard
              key={restaurant._id}
              placeId={restaurant.googlePlaceId}
              databaseId={restaurant._id}
            />
          ))}
        </View>
      )}

      {canLoadMore || isLoadingMore ? (
        <TouchableOpacity
          style={styles.loadMoreButton}
          onPress={() => placeIdsResult?.loadMore(PAGE_SIZE)}
          disabled={isLoadingMore}
          activeOpacity={0.7}
        >
          {isLoadingMore ? (
            <ActivityIndicator size="small" color="#4e2916" />
          ) : (
            <Text style={styles.loadMoreText}>Load more...</Text>
          )}
        </TouchableOpacity>
      ) : null}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F9F5F3",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  scrollContent: {
    flexGrow: 1,
    backgroundColor: "#F9F5F3",
    alignItems: "stretch",
    padding: 24,
    maxWidth: 640,
    width: "100%",
    alignSelf: "center",
  },
  headerCard: {
    width: "100%",
    backgroundColor: "#ffffff",
    borderRadius: 16,
    padding: 28,
    alignItems: "center",
    marginBottom: 24,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 6,
  },
  title: {
    fontSize: 28,
    fontWeight: "700",
    color: "#4e2916",
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    color: "#666",
    textAlign: "center",
    marginBottom: 16,
  },
  awesomeText: {
    fontSize: 20,
    fontWeight: "600",
    color: "#4e2916",
    textAlign: "center",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "600",
    color: "#4e2916",
    marginBottom: 12,
  },
  list: {
    gap: 24,
  },
  emptyText: {
    fontSize: 14,
    color: "#999",
    marginTop: 8,
  },
  loadMoreButton: {
    marginTop: 16,
    marginBottom: 24,
    paddingVertical: 12,
    paddingHorizontal: 20,
    minHeight: 44,
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "center",
  },
  loadMoreText: {
    fontSize: 15,
    fontWeight: "500",
    color: "#4e2916",
  },
  button: {
    backgroundColor: "#4e2916",
    borderRadius: 10,
    paddingVertical: 14,
    paddingHorizontal: 28,
    minWidth: 180,
    alignItems: "center",
  },
  buttonText: {
    color: "#ffffff",
    fontSize: 16,
    fontWeight: "600",
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: "#666",
  },
});
